import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { api, setAuthToken, clearAuthToken } from '@/api/client';

// LognAI Authentication Types
export interface User {
  id: string;
  email: string;
  fullName: string;
  companyId: string;
  company: {
    id: string;
    name: string;
    displayName: string;
    type: 'PRIMARY_ENTERPRISE' | 'PARTNER';
  };
  secondaryCompanyIds: string[];
  department?: string;
  jobTitle?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'LOCKED' | 'PENDING_VERIFICATION';
  roles: string[];
  permissions: string[];
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ACCESS_TOKEN_KEY = 'weatherford_access_token';
const REFRESH_TOKEN_KEY = 'weatherford_refresh_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (accessToken) {
        setAuthToken(accessToken);
        try {
          const response = await api.get<User>('/api/auth/me');
          setUser(response.data);
        } catch {
          // Token invalid, try to refresh
          const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
          if (refreshToken) {
            try {
              const refreshResponse = await api.post<AuthTokens>('/api/auth/refresh', {
                refreshToken,
              });
              localStorage.setItem(ACCESS_TOKEN_KEY, refreshResponse.data.accessToken);
              localStorage.setItem(REFRESH_TOKEN_KEY, refreshResponse.data.refreshToken);
              setAuthToken(refreshResponse.data.accessToken);
              const userResponse = await api.get<User>('/api/auth/me');
              setUser(userResponse.data);
            } catch {
              clearAuthToken();
              localStorage.removeItem(ACCESS_TOKEN_KEY);
              localStorage.removeItem(REFRESH_TOKEN_KEY);
            }
          }
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.post<AuthTokens & { user: User }>('/api/auth/login', {
      email,
      password,
    });

    const { accessToken, refreshToken, user } = response.data;

    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    setAuthToken(accessToken);
    setUser(user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Continue logout even if API call fails
    } finally {
      clearAuthToken();
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setUser(null);
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<AuthTokens>('/api/auth/refresh', {
      refreshToken,
    });

    localStorage.setItem(ACCESS_TOKEN_KEY, response.data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
    setAuthToken(response.data.accessToken);
  }, []);

  // Permission checking with wildcard support
  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user) return false;

      // Platform admin has all permissions
      if (user.permissions.includes('*:*')) return true;

      // Check exact match
      if (user.permissions.includes(permission)) return true;

      // Check wildcard matches (e.g., order:* matches order:create)
      const [resource, action] = permission.split(':');
      if (user.permissions.includes(`${resource}:*`)) return true;
      if (user.permissions.includes(`*:${action}`)) return true;

      return false;
    },
    [user]
  );

  const hasRole = useCallback(
    (role: string): boolean => {
      if (!user) return false;
      return user.roles.includes(role);
    },
    [user]
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]): boolean => {
      return permissions.some((p) => hasPermission(p));
    },
    [hasPermission]
  );

  const hasAllPermissions = useCallback(
    (permissions: string[]): boolean => {
      return permissions.every((p) => hasPermission(p));
    },
    [hasPermission]
  );

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshAuth,
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAllPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Hook for permission-based UI rendering
export function usePermission(permission: string): boolean {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
}

// Hook for role-based UI rendering
export function useRole(role: string): boolean {
  const { hasRole } = useAuth();
  return hasRole(role);
}
